let getAccessToken: () => string | null = () => null;
let onUnauthorized: () => void = () => {};

/** #1051 -- the header naming the tenant a request acts for (the server's ActiveTenantFilter). */
export const TENANT_HEADER = 'X-PeruseLab-Tenant';
/** The header value for the signed-in user's personal tenant. */
export const PERSONAL_TENANT = 'personal';
let activeTenantId: string | null = null;

/**
 * #1051 -- the organisation every request acts for from now on; `null` is the personal tenant.
 * Set by the app's tenant switcher.
 */
export function setActiveTenant(tenantId: string | null): void {
  activeTenantId = tenantId;
}

export function getActiveTenant(): string | null {
  return activeTenantId;
}

/**
 * #1051 -- headers that pin one request to a tenant instead of the active one (`null` = personal):
 * an open drawing keeps saving to the tenant it belongs to after the user switches (P6).
 * `undefined` adds nothing, so the request uses the active tenant.
 */
export function tenantHeaders(tenantId: string | null | undefined): Record<string, string> {
  return tenantId === undefined ? {} : { [TENANT_HEADER]: tenantId ?? PERSONAL_TENANT };
}

/** Wire the app's auth into the client. Call once at startup, before the first request. */
export function configureApiAuth(options: { getAccessToken: () => string | null; onUnauthorized: () => void }): void {
  getAccessToken = options.getAccessToken;
  onUnauthorized = options.onUnauthorized;
}

const configuredApiBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim();
const API_BASE_URL = (configuredApiBaseUrl || '/api').replace(/\/$/, '');

// #485: fetch() has no default timeout, so a backend call that stalls (a slow/unresponsive
// upstream, a DNS hiccup) hung every caller forever -- indistinguishable, from the UI's point of
// view, from nothing having happened at all. 20s comfortably exceeds the backend's own PubChem
// timeout budget (3s connect + 5s read, server/platform-core's RestClientConfig) plus normal
// network/ES latency, so this only ever fires for a genuinely stuck request. A caller whose
// backend budget is legitimately longer (OCSR, #878) passes its own `timeoutMs`.
const DEFAULT_TIMEOUT_MS = 20_000;

export class ApiNetworkError extends Error {
  constructor(
    message: string,
    readonly timedOut = false
  ) {
    super(message);
    this.name = 'ApiNetworkError';
  }
}

export async function apiFetch(
  path: string,
  init?: RequestInit,
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<Response> {
  const timeoutController = new AbortController();
  const timeout = globalThis.setTimeout(() => timeoutController.abort(), timeoutMs);
  try {
    const accessToken = getAccessToken();
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(activeTenantId ? { [TENANT_HEADER]: activeTenantId } : {}),
        // A caller's own headers win, including a tenant pinned by tenantHeaders().
        ...init?.headers
      },
      signal: timeoutController.signal
    });
    if (response.status === 401) {
      onUnauthorized();
    }
    return response;
  } catch (error) {
    if (timeoutController.signal.aborted) throw new ApiNetworkError('The request timed out.', true);
    throw new ApiNetworkError(error instanceof Error ? error.message : 'Unknown network error.');
  } finally {
    globalThis.clearTimeout(timeout);
  }
}
